import { BundleBuilderEntry } from './useBundleBuilderCatalog';

/**
 * Presentation constants shared with the marketing Bundle Builder at traineracademy.org.
 *
 * The landing page keeps tiers, prices and copy in its own config module; here everything that can
 * come from the API does (prices, membership, names, cover art), and only the things the API has no
 * column for live below — brand accents and the box renders.
 *
 * Assets are served from the same CDN the landing page uses, keyed by the course `slug`. Our slugs
 * were chosen to match it exactly (cpt/cnc/cbs/ces/csc/gx), so a cert with a slug gets its artwork
 * for free and stays visually identical across the two surfaces.
 */

const CDN = 'https://ipta-cdn.b-cdn.net/bundle';

/** Textbook cover art. Only used when admin has not set an explicit coverImageUrl on the course. */
export const cdnCoverForSlug = (slug?: string) => (slug ? `${CDN}/cover-${slug}.webp` : undefined);

/** The little triangle mark that appears next to each certification. */
export const cdnIconForSlug = (slug?: string) => (slug ? `${CDN}/icons/a-${slug}.png` : undefined);

/**
 * The spine texture drawn inside the box.
 *
 * `photo8` is the Tyler-approved set: each book from the master hero shot, un-warped out of its
 * slot quad into a flat rectangle with the baked icons erased and titles registered to a shared
 * baseline — which is what lets one texture sit convincingly in any slot of any box.
 */
export const cdnSpineTexture = (slug?: string) => (slug ? `${CDN}/photo8-${slug}.webp` : undefined);

/**
 * Slot order for the full box.
 *
 * Matches the hero render (Group Exercise 5th, Strength & Conditioning 6th) so every texture sits
 * in the slot it was photographed in — the lighting baked into each crop then lines up with the
 * lighting of the box behind it.
 */
export const MASTER_SLOT_ORDER = ['cpt', 'cnc', 'cbs', 'ces', 'gx', 'csc'];

/** The NCCA badge. Hosted on the marketing site rather than the CDN — the only asset that is. */
export const NCCA_BADGE_URL = 'https://traineracademy.org/CPT/ncca-white.png.webp';

/**
 * Named quick-pick combinations, mirroring the marketing builder.
 *
 * These are shortcuts to selections the learner could make by hand, never separate products: the
 * bundle they resolve to is still looked up by exact member set like any other pick.
 */
export type StackPreset = { id: string; name: string; specialty: string; description: string };

/** Every Elite stack rides on CPT + Strength & Conditioning + Bodybuilding; the stack picks the fourth. */
export const ELITE_STACK_EXTRAS = ['csc', 'cbs'];

export const ELITE_STACKS: StackPreset[] = [
  { id: 'prime', name: 'PRIME Stack', specialty: 'ces', description: 'The full PRIME training method.' },
  { id: 'coaching', name: 'Coaching Stack', specialty: 'cnc', description: 'Training plus nutrition.' },
  { id: 'studio', name: 'Studio Stack', specialty: 'gx', description: 'Built for the gym floor.' },
];

export const ESSENTIAL_STACKS: StackPreset[] = [
  { id: 'ess-nutrition', name: 'CPT + Nutrition', specialty: 'cnc', description: 'The most popular pairing.' },
  { id: 'ess-strength', name: 'CPT + S&C', specialty: 'csc', description: 'Performance-focused pairing.' },
  { id: 'ess-bodybuilding', name: 'CPT + Bodybuilding', specialty: 'cbs', description: 'Physique-focused pairing.' },
  { id: 'ess-corrective', name: 'CPT + Corrective', specialty: 'ces', description: 'Post-rehab and older adults.' },
  { id: 'ess-group', name: 'CPT + Group', specialty: 'gx', description: 'Classes and group training.' },
];

/**
 * The box render, keyed by how many certifications are in the package.
 *
 * Only 2, 4 and 6 exist — the three package sizes IPTA sells. Any other count (a mis-tagged bundle)
 * gets no image rather than a broken one.
 */
export const cdnBoxForCertCount = (count: number) =>
  count === 2 || count === 4 || count === 6 ? `${CDN}/box-${count}.webp` : undefined;

/** Brand colour per certification, matching the spine colours on the box renders. */
export const CERT_ACCENTS: Record<string, string> = {
  cpt: '#ED5A26',
  cnc: '#22be74',
  cbs: '#38B6FF',
  ces: '#8B5E3C',
  csc: '#8B5CF6',
  gx: '#F5B700',
};

export const BRAND_ORANGE = '#ED5A26';
export const BRAND_INK = '#141826';
export const BRAND_GREEN = '#22be74';

export const accentForSlug = (slug?: string) => (slug && CERT_ACCENTS[slug]) || BRAND_ORANGE;

/** Certification code per slug — the landing config's `code` field, and the suffix of the LMS name. */
export const CERT_CODES: Record<string, string> = {
  cpt: 'CPT',
  cnc: 'CNS',
  cbs: 'CBS',
  ces: 'CES',
  csc: 'SCS',
  gx: 'GES',
};

export const certCodeForSlug = (slug?: string) => (slug && CERT_CODES[slug]) || (slug ?? '').toUpperCase();

export const TIER_ORDER: BundleBuilderEntry['tier'][] = ['master', 'elite', 'essential'];

export const TIER_LABEL: Record<BundleBuilderEntry['tier'], string> = {
  master: 'Master Trainer',
  elite: 'Elite Trainer',
  essential: 'Essential Trainer',
};

/**
 * The strapline under each package name.
 *
 * Derived from the package's real membership rather than hardcoded, so a bundle tagged Elite but
 * built with the wrong number of certifications describes itself honestly instead of claiming
 * "CPT + 3" while containing two.
 */
export const tierBlurb = (tier: BundleBuilderEntry['tier'], certCount: number) => {
  if (tier === 'master') return `All ${certCount} certs`;
  if (tier === 'essential') return certCount === 2 ? 'CPT + 1 cert free' : `CPT + ${certCount - 1} certs`;
  return `CPT + ${certCount - 1} cert${certCount - 1 === 1 ? '' : 's'}`;
};

/**
 * All price fields are whole DOLLARS — production's convention, which PricingTable and
 * PaymentModal render raw. (This environment briefly held cents after a Stripe sync wrote
 * unit_amount into the DB; that data was converted and the sync script fixed. If prices ever
 * display 100x too high again, the DATA has regressed to cents — fix it there, not here.)
 */
export const formatUsd = (amount?: number) =>
  typeof amount === 'number' ? `$${Math.round(amount).toLocaleString('en-US')}` : undefined;

/** Kept as an alias so call sites read naturally for either name. */
export const formatDollars = formatUsd;

/**
 * Monthly installment for the advertised payment plan.
 *
 * Installment schedules have a $49 charge on day 1 (via first-month coupon) and 11
 * monthly payments thereafter (with day-5 follow-up payment eliminated):
 *   Master:    (1468 - 49) / 11 = $129/mo
 *   Elite:     (1248 - 49) / 11 = $109/mo
 *   Essential: (1028 - 49) / 11 =  $89/mo
 */
export const PLAN_START_DOLLARS = 49;

export const planMonthlyDollars = (totalDollars?: number, dayFiveDollars?: number) =>
  typeof totalDollars === 'number'
    ? Math.round((totalDollars - PLAN_START_DOLLARS - (dayFiveDollars ?? 0)) / 11)
    : undefined;

/** The strike-through saving the landing page shows: plan total vs paying cash once. */
export const planSavingsDollars = (purchaseDollars?: number, installmentDollars?: number) =>
  typeof purchaseDollars === 'number' && typeof installmentDollars === 'number' && installmentDollars > purchaseDollars
    ? Math.round(installmentDollars - purchaseDollars)
    : undefined;
